import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';
import EmailIcon from '@mui/icons-material/Email';
import CircleIcon from '@mui/icons-material/Circle';

const GmailAccountManager = () => {
  const [accounts, setAccounts] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [newAccount, setNewAccount] = useState({
    email: '',
    password: '',
    name: ''
  });
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/gmail-accounts`);
      setAccounts(response.data);
    } catch (error) {
      console.error('Failed to fetch Gmail accounts:', error);
      setError('Failed to load Gmail accounts');
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/gmail-accounts/auth-url`, {
        accountName: newAccount.name
      });
      window.location.href = response.data.authUrl;
    } catch (error) {
      console.error('Failed to initiate Google Sign-in:', error);
      setError('Failed to start Google authentication');
    }
  };

  const handleDeleteAccount = async (accountId) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/gmail-accounts/${accountId}`);
      setAccounts(accounts.filter(account => account._id !== accountId));
    } catch (error) {
      console.error('Failed to delete Gmail account:', error);
      setError('Failed to delete Gmail account');
    }
  };

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '24px' }}>
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 'regular' }}>Gmail Accounts</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
            sx={{
              backgroundColor: '#1976d2',
              textTransform: 'none',
              borderRadius: 1,
            }}
          >
            Add Account
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
        <List sx={{ p: 0 }}>
          {accounts.map((account) => (
            <ListItem
              key={account._id}
              sx={{
                py: 2.5,
                px: 3,
                my: 1,
                borderRadius: 2,
                backgroundColor: '#f8f9fa',
                border: '1px solid #e0e0e0',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: '#ffffff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              <Box
                sx={{
                  mr: 2.5,
                  color: '#3a86ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(58, 134, 255, 0.08)',
                  borderRadius: '12px',
                  p: 1,
                }}
              >
                <EmailIcon sx={{ fontSize: 28 }} />
              </Box>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: '#2c3e50',
                      }}
                    >
                      {account.accountName}
                    </Typography>
                    <CircleIcon
                      sx={{
                        fontSize: 8,
                        ml: 1.5,
                        color: '#34d399',
                      }}
                    />
                  </Box>
                }
                secondary={
                  <Box sx={{ color: '#64748b' }}>
                    {account.email}
                    <br />

                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={() => handleDeleteAccount(account._id)}
                  sx={{
                    color: '#94a3b8',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: '#ef4444',
                      backgroundColor: 'rgba(239, 68, 68, 0.08)',
                    }
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Paper>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Add Gmail Account</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Account Name"
            fullWidth
            value={newAccount.name}
            onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            fullWidth
            onClick={handleGoogleSignIn}
            sx={{ mt: 2 }}
          >
            Sign in with Google
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default GmailAccountManager;